import XLSX from 'xlsx-js-style'
import { saveAs } from 'file-saver'

function s2ab (s) {
  const buf = new ArrayBuffer(s.length)
  const view = new Uint8Array(buf)
  for (let i = 0; i !== s.length; ++i) view[i] = s.charCodeAt(i) & 0xFF
  return buf
}

const HEADER_FILL = '2E7D32'
const HEADER_FILL_LIGHT = '43A047'
const HEADER_FONT = 'FFFFFF'
const ALTERNATE_FILL = 'F2F7F2'
const BORDER_COLOR = 'CFD8DC'
const SUMMARY_FILL = 'E8F5E9'
const SUMMARY_BORDER = '2E7D32'

const fmt = (val) => (typeof val === 'number' ? parseFloat(val.toFixed(2)) : val)

function buildColumns (groupLabel) {
  return [
    { label: 'Team', value: item => item.teamLabel || '', align: 'left', bold: true },
    { label: groupLabel, value: item => item.groupLabel || item.group || '', align: 'left', bold: true },
    { label: 'No of Employees', value: item => item.employees || 0, align: 'right', numFmt: '0' },
    { label: 'Total Working Days', value: item => fmt(item.workingDays), align: 'right' },
    { label: 'Expected working hrs', value: item => fmt(item.expectedHrs), align: 'right' },
    { label: 'Actual Working Hours', value: item => fmt(item.actualHrs), align: 'right' },
    { label: 'Absence/Leave hours', value: item => fmt(item.absenceLeaveHrs), align: 'right' },
    { label: 'Absent %', value: item => fmt(item.absentPercent), align: 'right', numFmt: '0.00' },
    { label: 'OT Hours After work', value: item => fmt(item.otAfterWork), align: 'right' },
    { label: 'OT Rest', value: item => fmt(item.otRest), align: 'right' },
    { label: 'OT Night', value: item => fmt(item.otNight), align: 'right' },
    { label: 'OT Holiday', value: item => fmt(item.otHoliday), align: 'right' },
  ]
}

function buildTitleRows (colCount, companyHeader, title, dateRangeText) {
  const rows = []
  const merges = []
  const titleRows = []

  const addRow = (text, size) => {
    const row = new Array(colCount).fill('')
    row[0] = text
    const r = rows.length
    rows.push(row)
    merges.push({ s: { r, c: 0 }, e: { r, c: colCount - 1 } })
    titleRows.push({ r, size })
  }

  if (companyHeader) addRow(companyHeader, 14)
  addRow(title, 14)
  addRow(dateRangeText, 11)
  rows.push(new Array(colCount).fill(''))
  return { rows, merges, titleRows }
}

function buildSheet (columns, titleInfo, rows, dataRows, teamBlocks, colWidths) {
  const colCount = columns.length
  const headerRowIdx = rows.length
  rows.push(columns.map(c => c.label))
  dataRows.forEach(r => rows.push(r))

  const dataOffset = headerRowIdx + 1
  const ws = XLSX.utils.aoa_to_sheet(rows)
  ws['!merges'] = [
    ...titleInfo.merges,
    ...(teamBlocks || [])
      .filter(b => b.end > b.start)
      .map(b => ({ s: { r: dataOffset + b.start, c: 0 }, e: { r: dataOffset + b.end, c: 0 } })),
  ]
  ws['!cols'] = colWidths || columns.map(() => ({ wch: 14 }))

  const applyStyle = (addr, style) => {
    if (!ws[addr]) return
    if (!ws[addr].s) ws[addr].s = {}
    ws[addr].s = Object.assign({}, ws[addr].s, style)
  }

  const borderRight = { style: 'thin', color: { rgb: BORDER_COLOR } }
  const borderBottom = { style: 'thin', color: { rgb: BORDER_COLOR } }

  titleInfo.titleRows.forEach(({ r, size: sz }) => {
    for (let c = 0; c < colCount; c++) {
      applyStyle(XLSX.utils.encode_cell({ r, c }), {
        font: { bold: true, sz, color: { rgb: HEADER_FONT } },
        fill: { fgColor: { rgb: HEADER_FILL }, patternType: 'solid' },
        alignment: { horizontal: 'center', vertical: 'center' },
      })
    }
  })

  for (let c = 0; c < colCount; c++) {
    applyStyle(XLSX.utils.encode_cell({ r: headerRowIdx, c }), {
      font: { bold: true, color: { rgb: HEADER_FONT } },
      fill: { fgColor: { rgb: HEADER_FILL_LIGHT }, patternType: 'solid' },
      alignment: { horizontal: 'center', vertical: 'center', wrapText: true },
      border: { right: borderRight },
    })
  }

  const range = XLSX.utils.decode_range(ws['!ref'])
  const rangeStart = headerRowIdx + 1

  for (let r = rangeStart; r <= range.e.r; r++) {
    const kind = rows[r] && rows[r].__kind
    const isTotal = kind === 'total'
    const isSubtotal = kind === 'subtotal'
    const isAlt = !isTotal && !isSubtotal && (r - rangeStart) % 2 === 1
    for (let c = 0; c <= range.e.c; c++) {
      const addr = XLSX.utils.encode_cell({ r, c })
      if (!ws[addr]) continue
      const style = {
        alignment: {
          wrapText: true,
          vertical: 'center',
          horizontal: (columns[c] && columns[c].align) || 'left',
        },
      }
      if (isTotal) {
        style.font = { bold: true }
        style.fill = { fgColor: { rgb: SUMMARY_FILL }, patternType: 'solid' }
        style.border = {
          top: { style: 'thick', color: { rgb: SUMMARY_BORDER } },
          bottom: { style: 'thick', color: { rgb: SUMMARY_BORDER } },
        }
      } else if (isSubtotal) {
        style.font = { bold: true }
        style.fill = { fgColor: { rgb: ALTERNATE_FILL }, patternType: 'solid' }
        style.border = {
          right: borderRight,
          bottom: { style: 'medium', color: { rgb: BORDER_COLOR } },
        }
      } else if (isAlt) {
        style.fill = { fgColor: { rgb: ALTERNATE_FILL }, patternType: 'solid' }
        style.border = { right: borderRight, bottom: borderBottom }
      } else {
        style.border = { right: borderRight, bottom: borderBottom }
      }
      if (c <= 1 && columns[c] && columns[c].bold && !isTotal && !isSubtotal) {
        style.font = Object.assign({}, style.font, { bold: true })
      }
      if (columns[c] && columns[c].numFmt) {
        style.numFmt = columns[c].numFmt
      }
      applyStyle(addr, style)
    }
  }

  return ws
}

export function exportManHourToExcel ({
  teams,
  total,
  companyHeader,
  title,
  dateRangeText,
  groupLabel,
  filename,
}) {
  const columns = buildColumns(groupLabel)
  const colCount = columns.length
  const wb = XLSX.utils.book_new()

  const titleInfo = buildTitleRows(colCount, companyHeader, title, dateRangeText)
  const rows = titleInfo.rows
  const dataRows = []
  const teamBlocks = []

  const pushRow = (item, kind) => {
    const row = columns.map(c => c.value(item))
    if (kind) row.__kind = kind
    dataRows.push(row)
  }

  ;(teams || []).forEach(t => {
    const startIdx = dataRows.length
    ;(t.rows || []).forEach((row, i) => pushRow({ ...row, teamLabel: i === 0 ? (t.team || '') : '' }))
    if (t.rows && t.rows.length) pushRow({ ...t.subtotal, teamLabel: '', groupLabel: 'Subtotal' }, 'subtotal')
    teamBlocks.push({ start: startIdx, end: dataRows.length - 1 })
  })

  if (total) pushRow({ ...total, teamLabel: '', groupLabel: 'Total' }, 'total')

  const colWidths = [
    { wch: 16 },
    { wch: 22 },
    { wch: 10 },
    { wch: 11 },
    { wch: 12 },
    { wch: 12 },
    { wch: 12 },
    { wch: 9 },
    { wch: 11 },
    { wch: 9 },
    { wch: 9 },
    { wch: 10 },
  ]

  const ws = buildSheet(columns, titleInfo, rows, dataRows, teamBlocks, colWidths)
  XLSX.utils.book_append_sheet(wb, ws, 'Man Hour')

  const wbout = XLSX.write(wb, { bookType: 'xlsx', type: 'binary' })
  saveAs(new Blob([s2ab(wbout)], { type: 'application/octet-stream' }), `${filename}.xlsx`)
  return true
}
